import React, { Component } from 'react';
import PropTypes from 'prop-types';
import inflection from 'inflection';
import {
  getResources,
  setSidebarVisibility,
  MenuItemLink,
  withTranslate
} from 'react-admin';
import { withRouter } from 'react-router';

import classnames from 'classnames';
import { withStyles, createStyles } from '@material-ui/core/styles';

import { connect } from 'react-redux';
import SubMenu from './SubMenu';

import { CollapseButton } from './CollapseButton';

const styles = theme =>
  createStyles({
    root: {
      color: '#424242',
      display: 'flex',
      alignItems: 'flex-start',
      borderLeft: '4px solid transparent',
      paddingLeft: '16px',
      paddingRight: props => (props.open ? '16px' : '28px'),
      paddingTop: '8px',
      paddingBottom: '8px',
      '& div': {
        display: 'flex',
        alignItems: 'flex-start',
        paddingRight: '0px',
        minWidth: '28px'
      },
      '& span': {
        minWidth: '20px',
        minHeight: '20px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }
    },
    active: {
      color: '#424242',
      'border-left': '4px solid',
      'border-color': theme.palette.secondary.main,
      'background-color': '#E3F2FD'
    },
    icon: { color: '#757575', height: '1.16em' }
  });
const subMenuText = {
  sidebarOpened: {
    paddingLeft: '2rem',
    transition: 'padding-left 195ms cubic-bezier(0.4, 0, 0.6, 1) 0ms'
  },
  sidebarClosed: {
    paddingLeft: '1rem',
    transition: 'padding-left 195ms cubic-bezier(0.4, 0, 0.6, 1) 0ms'
  }
};

const menuStyle = {
  width: 'inherit',
  height: 'inherit'
};
const collapsedMenuStyle = {
  width: '20px',
  height: 'inherit'
};

const translatedResourceName = (resource, translate) => {
  const name = translate(`resources.${resource.name}.name`, {
    smart_count: 2,
    _:
      resource.options && resource.options.label
        ? translate(resource.options.label, {
            smart_count: 2,
            _: resource.options.label
          })
        : inflection.humanize(inflection.pluralize(resource.name))
  });
  return name;
};

const MENU_STATE_KEY = 'menustate';

class SolsticeMenu extends Component {
  static propTypes = {
    onMenuClick: PropTypes.func,
    logout: PropTypes.object,
    classes: PropTypes.object.isRequired,
    className: PropTypes.string
  };

  constructor(props) {
    super(props);

    // Get saved menu state from local storage
    this.state = {
      menu: {}
    };
    const menuState = sessionStorage.getItem(MENU_STATE_KEY);
    if (menuState !== null) {
      try {
        this.state.menu = JSON.parse(menuState);
      } catch (e) {
        // Wrong JSON format, do nothing
      }
    }
  }

  componentDidMount = () => {
    // Get menu state from initial route
    this.onRouteChanged(this.props.resources, this.props.location.pathname);
  };

  componentDidUpdate(prevProps) {
    if (this.props.location.pathname !== prevProps.location.pathname) {
      // New route, update menu state
      this.onRouteChanged(this.props.resources, this.props.location.pathname);
    }
  }

  /**
   * Route has changed, update menu state
   */
  onRouteChanged = (resources, nextLocation) => {
    // Find 1st selected resource
    const resource = resources.find(
      resource =>
        resource.name === nextLocation.substr(1) &&
        resource.options.group !== undefined
    );
    // Select submenu containing this resource
    if (resource !== undefined && resource.options.group) {
      this.setState(
        {
          menu: {
            ...this.state.menu,
            [resource.options.group]: true
          }
        },
        () => this.saveStore()
      );
    }
  };

  /**
   * Save menu state to local storage
   */
  saveStore = () => {
    const json = JSON.stringify(this.state.menu);
    sessionStorage.setItem(MENU_STATE_KEY, json);
  };

  /**
   * Toggle menu open in state
   */
  handleToggle = group => {
    this.setState(
      {
        menu: {
          ...this.state.menu,
          [group]: !this.state.menu[group]
        }
      },
      () => this.saveStore()
    );
  };

  /**
   * Build menu tree from resource list
   * @param {*} resources
   */
  buildMenuTree(resources) {
    // Index group by group name
    const groups = {};

    // Build the menu tree
    return resources
      .filter(resource => resource.hasList)
      .reduce((menu, resource) => {
        if (resource.options.group !== undefined) {
          const { group, groupIcon } = resource.options;
          let groupMenu = groups[group];
          if (groupMenu === undefined) {
            groupMenu = {
              label: group,
              groupIcon: groupIcon,
              children: [resource]
            };
            groups[group] = groupMenu;
            menu.push(groupMenu);
          } else {
            groupMenu.children.push(resource);
          }
        } else {
          menu.push(resource);
        }
        return menu;
      }, []);
  }

  render() {
    const {
      onMenuClick,
      classes,
      className,
      dense,
      resources,
      translate,
      open,
      setSidebarVisibility
    } = this.props;

    const tree = this.buildMenuTree(resources);
    return (
      <div style={open ? menuStyle : collapsedMenuStyle}>
        {tree.map(resource => {
          // Top level menu item
          if (resource.children === undefined) {
            return (
              <MenuItemLink
                id={translatedResourceName(resource, translate)}
                className={classnames(classes.root, className)}
                activeClassName={classes.active}
                key={resource.name}
                to={`/${resource.name}`}
                primaryText={translatedResourceName(resource, translate)}
                leftIcon={
                  resource.icon ? (
                    <resource.icon
                      className={classnames(classes.icon, className)}
                    />
                  ) : null
                }
                onClick={onMenuClick}
                dense={dense}
              />
            );
          } else {
            // Sub-menu with children
            return (
              <SubMenu
                handleToggle={() => this.handleToggle(resource.label)}
                isOpen={this.state.menu[resource.label]}
                sidebarIsOpen={open}
                name={resource.label}
                key={resource.label}
                icon={resource.groupIcon}
                style={{ rgba: '(0, 0, 0, 80)' }}
              >
                {resource.children.map(child => (
                  <MenuItemLink
                    id={translatedResourceName(child, translate)}
                    className={classnames(classes.root, className)}
                    activeClassName={classes.active}
                    key={child.name}
                    to={`/${child.name}`}
                    primaryText={translatedResourceName(child, translate)}
                    leftIcon={
                      child.icon ? (
                        <child.icon
                          className={classnames(classes.icon, className)}
                        />
                      ) : null
                    }
                    onClick={onMenuClick}
                    dense={dense}
                    style={
                      open
                        ? subMenuText.sidebarOpened
                        : subMenuText.sidebarClosed
                    }
                  />
                ))}
              </SubMenu>
            );
          }
        })}
        <CollapseButton
          open={this.props.open}
          setVisibility={setSidebarVisibility}
        />
      </div>
    );
  }
}

const mapStateToProps = state => ({
  open: state.admin.ui.sidebarOpen,
  resources: getResources(state),
  pathname: state.router.location.pathname // used to force redraw on navigation
});

export default withTranslate(
  connect(mapStateToProps, { setSidebarVisibility })(
    withRouter(withStyles(styles)(SolsticeMenu))
  )
);
